export enum LabelSize {
  NORMAL = 'normal',
  SMALL = 'small',
  ALT = 'alt',
  JEWELRY = 'jewelry',
}

const labelSizes = Object.values(LabelSize);

export enum BarcodeDisplayOptions {
  BARCODE_AND_NUMBERS = 'barcode_and_numbers',
  NUMBERS = 'numbers',
  BARCODE = 'barcode',
}

// '8' EAN-8 (8 digits) used for systemSku on small/jewelry
// 'E' EAN-13 (13 digits)
// 'U' UPC-A (12 digits)
// 'C' Code128 (any digits) used for printing short or long barcodes
export type BarcodeChar = '8' | 'E' | 'U' | 'C';

export enum BarcodeTypes {
  SYSTEM_SKU = 'systemSku',
  UPC = 'upc',
  EAN = 'ean',
  CUSTOM_SKU = 'customSku',
  MANUFACTURER_SKU = 'manufacturerSku',
}

export enum LabelContent {
  NONE = 'none',
  MSRP = 'msrp',
  NOTE = 'Note',
  CUSTOM_SKU = 'customSku',
  MANUFACTURER_SKU = 'manufacturerSku',
  CATEGORY = 'Category',
  MANUFACTURER = 'Manufacturer',
  VENDOR_ID = 'Vendor',
  PRINT_TIMESTAMP = 'printTimestamp',
  MATRIX_ATTRIBUTES = 'ItemMatrix',
}
interface SupportedOption {
  label: string;
  value: BarcodeDisplayOptions | BarcodeTypes | LabelContent;
}
export interface ActiveOptions extends SupportedOption {
  active: boolean;
  filter: (value: LabelDataItem) => boolean;
}

export const BARCODE_DISPLAY_OPTIONS: SupportedOption[] = [
  { label: 'Barcode and numbers', value: BarcodeDisplayOptions.BARCODE_AND_NUMBERS },
  { label: 'Numbers, letters and special characters', value: BarcodeDisplayOptions.NUMBERS },
  { label: 'Barcode only', value: BarcodeDisplayOptions.BARCODE },
];

export const LABEL_CONTENT_OPTIONS: SupportedOption[] = [
  { label: 'None', value: LabelContent.NONE },
  { label: 'MSRP', value: LabelContent.MSRP },
  { label: 'Note', value: LabelContent.NOTE },
  { label: 'Custom SKU', value: LabelContent.CUSTOM_SKU },
  { label: 'Manufacturer SKU', value: LabelContent.MANUFACTURER_SKU },
  { label: 'Category', value: LabelContent.CATEGORY },
  { label: 'Manufacturer', value: LabelContent.MANUFACTURER },
  { label: 'Vendor ID', value: LabelContent.VENDOR_ID },
  { label: "Print today's date", value: LabelContent.PRINT_TIMESTAMP },
  { label: 'Matrix attributes', value: LabelContent.MATRIX_ATTRIBUTES },
];

export const LABEL_CONTENT_ACTIVE_OPTIONS: ActiveOptions[] = [
  { label: 'None', value: LabelContent.NONE, active: true, filter: () => true },
  {
    label: 'MSRP',
    value: LabelContent.MSRP,
    active: false,
    filter: (Item) =>
      !!Item.Prices.ItemPrice.filter(
        (priceType) => priceType.useType === 'MSRP' && priceType.amount !== '0'
      ).length,
  },
  {
    label: 'Note',
    value: LabelContent.NOTE,
    active: false,
    filter: (Item) => Item[LabelContent.NOTE]?.note !== '',
  },
  {
    label: 'Custom SKU',
    value: LabelContent.CUSTOM_SKU,
    active: false,
    filter: (Item) => Item[LabelContent.CUSTOM_SKU] !== '',
  },
  {
    label: 'Manufacturer SKU',
    value: LabelContent.MANUFACTURER_SKU,
    active: false,
    filter: (Item) => Item[LabelContent.MANUFACTURER_SKU] !== '',
  },
  {
    label: 'Category',
    value: LabelContent.CATEGORY,
    active: false,
    filter: (Item) => !!Item[LabelContent.CATEGORY]?.name,
  },
  {
    label: 'Brand',
    value: LabelContent.MANUFACTURER,
    active: false,
    filter: (Item) => !!Item[LabelContent.MANUFACTURER]?.name,
  },
  {
    label: 'Vendor ID',
    value: LabelContent.VENDOR_ID,
    active: false,
    filter: (Item) => !!Item[LabelContent.VENDOR_ID]?.name,
  },
  {
    label: "Print today's date",
    value: LabelContent.PRINT_TIMESTAMP,
    active: true,
    filter: () => true,
  },
  {
    label: 'Matrix attributes',
    value: LabelContent.MATRIX_ATTRIBUTES,
    active: false,
    filter: (Item) => !!Item[LabelContent.MATRIX_ATTRIBUTES],
  },
];

export const BARCODE_TYPES: ActiveOptions[] = [
  {
    label: 'System ID (Default)',
    value: BarcodeTypes.SYSTEM_SKU,
    active: true,
    filter: () => true,
  },
  {
    label: 'UPC',
    value: BarcodeTypes.UPC,
    active: false,
    filter: (Item) => Item[BarcodeTypes.UPC] !== '',
  },
  {
    label: 'EAN',
    value: BarcodeTypes.EAN,
    active: false,
    filter: (Item) => Item[BarcodeTypes.EAN] !== '',
  },
  {
    label: 'Custom SKU',
    value: BarcodeTypes.CUSTOM_SKU,
    active: false,
    filter: (Item) => Item[BarcodeTypes.CUSTOM_SKU] !== '',
  },
  {
    label: 'Manufacturer SKU',
    value: BarcodeTypes.MANUFACTURER_SKU,
    active: false,
    filter: (Item) => Item[BarcodeTypes.MANUFACTURER_SKU] !== '',
  },
];
interface SupportedLabel {
  label: string;
  value: LabelSize;
  dimensions: string;
}

export const SUPPORTED_LABELS: Readonly<SupportedLabel[]> = [
  { label: 'Small', value: LabelSize.SMALL, dimensions: '1.25" x 1.00"' },
  { label: 'Normal', value: LabelSize.NORMAL, dimensions: '2.25" x 1.25"' },
  { label: 'Alt', value: LabelSize.ALT, dimensions: '2.00" x 1.00"' },
  { label: 'Jewelry', value: LabelSize.JEWELRY, dimensions: '2.20" x 0.5"' },
];

export const isLabelSize = (str: string | undefined | null): str is LabelSize =>
  !!str && labelSizes.includes(str as LabelSize);

export type LabelMargins = {
  [key in LabelSize]: {
    left: number;
    top: number;
  };
};

interface LabelItemPrice {
  amount: string;
  useTypeID: string;
  useType: string;
}

interface LabelItemCategory {
  categoryID: string;
  name: string;
  fullPathName: string;
  createTime: string;
  parentID: string;
}

interface LabelItemNote {
  isPublic: boolean;
  note: string;
  noteID: string;
  timeStamp: string;
}

export interface LabelItemManufacturer {
  name: string;
  manufacturerID: string;
  createTime: string;
}

interface LabelItemCategory {
  categoryID: string;
  createTime: string;
  fullPathName: string;
  name: string;
  timeStamp: string;
}
export interface LabelItemVendor {
  accountNumber: string;
  archived: string;
  contactID: string;
  currencyRateID: string;
  integrationAccount: string;
  integrationEnabled: string;
  integrationLogin: string;
  integrationName: string;
  integrationPassword: string;
  name: string;
  noteID: string;
  priceLevel: string;
  shareSellThrough: string;
  updateCost: string;
  updateDescription: string;
  updatePrice: string;
  vendorID: string;
}

export interface LabelItemMatrix {
  Prices: { ItemPrice: LabelItemPrice[] };
  archived: string;
  attribute1Values: string;
  attribute2Values: string;
  attribute3Values: string;
  categoryID: string;
  defaultCost: string;
  defaultVendorID: string;
  departmentID: string;
  description: string;
  itemAttributeSetID: string;
  itemECommerceID: string;
  itemMatrixID: string;
  itemType: string;
  manufacturerID: string;
  modelYear: string;
  seasonID: string;
  serialized: string;
  tax: string;
  taxClassID: string;
}

export interface LabelItemAttributes {
  itemAttributesID: string;
  itemAttributeSetID: string;
  attribute1: string;
  attribute2: string;
  attribute3: string;
}

interface LabelDataOrder {
  vendorID: string;
  Vendor?: {
    name?: string;
    vendorID?: string;
  };
}

export interface LabelData {
  copies: string;
  itemID: string;
  // eslint-disable-next-line camelcase
  m: { title: string; currency_symbol: string; price: string; msrp?: string };
  Item: LabelDataItem;
  Order?: LabelDataOrder;
}

export interface ItemVendorNums {
  ItemVendorNum: ItemVendorNum[] | ItemVendorNum;
}

export interface ItemVendorNum {
  catalogVendorItemID: string;
  cost: string;
  itemID: string;
  itemVendorNumID: string;
  timeStamp: string;
  value: string;
  vendorID: string;
}
export interface LabelDataItem {
  archived: string;
  avgCost: string;
  categoryID: string;
  createTime: string;
  customSku: string;
  defaultCost: string;
  defaultVendorID: string;
  departmentID: string;
  description: string;
  discountable: string;
  ean: string;
  itemAttributesID: string;
  itemID: string;
  itemMatrixID: string;
  itemType: string;
  manufacturerID: string;
  manufacturerSku: string;
  modelYear: string;
  noteID: string;
  publishToEcom: string;
  seasonID: string;
  serialized: string;
  systemSku: string;
  tax: string;
  taxClassID: string;
  timeStamp: string;
  upc: string;
  Prices: { ItemPrice: LabelItemPrice[] };
  Category?: LabelItemCategory;
  ItemMatrix?: LabelItemMatrix;
  ItemAttributes?: LabelItemAttributes;
  Manufacturer?: LabelItemManufacturer;
  Note?: LabelItemNote;
  Vendor?: LabelItemVendor;
  ItemVendorNums?: ItemVendorNums;
}

export interface ApiResponse {
  Label?: LabelData[] | LabelData;
}

export interface LabelEditorItem {
  shopName: string;
  itemPrice: string;
  itemDescription: string;
  barcode: {
    ean: string;
    upc: string;
    systemSku: string;
  };
  matrixAttributes: string;
  brand: string;
  vendorId: string;
  todaysDate: string;
  msrp: string;
  manufacturerSku: string;
  customSku: string;
  quantity: number;
  category: {
    name: string;
    fullPathName: string;
  };
  systemID: string;
  freeForm: string | number | undefined;
}

export enum LabelTemplateType {
  LABEL = 'label',
  RECEIPT = 'receipt',
}

export interface LabelTemplate {
  printTemplateID: number;
  name: string;
  type: LabelTemplateType;
  content: string;
  timeStamp: Date;
}
